import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

function collectOptions(children, out = []) {
  React.Children.forEach(children, (child) => {
    if (child == null || child === false) return;
    if (Array.isArray(child)) {
      collectOptions(child, out);
      return;
    }
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collectOptions(child.props.children, out);
      return;
    }
    if (child.type === 'option' || child.type === 'optgroup') {
      if (child.type === 'option') {
        out.push({
          value: child.props.value != null ? String(child.props.value) : '',
          label: child.props.children,
          disabled: !!child.props.disabled,
        });
      } else {
        collectOptions(child.props.children, out);
      }
    }
  });
  return out;
}

function labelText(label) {
  if (label == null || typeof label === 'boolean') return '';
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  if (Array.isArray(label)) return label.map(labelText).join('');
  if (React.isValidElement(label)) return labelText(label.props?.children);
  return String(label);
}

/**
 * Custom select: Menu-Breite = Trigger-Breite (kein natives OS-Popup, das übersteht).
 * API wie natives <select> (value / onChange mit e.target.value, option-children).
 */
const SelectField = forwardRef(function SelectField(
  {
    className = 'input-field',
    wrapperClassName = '',
    chevronClassName = '',
    chevronSize = 'w-4 h-4',
    children,
    value,
    defaultValue,
    onFocus,
    onBlur,
    onChange,
    onMouseDown,
    onKeyDown,
    disabled,
    name,
    id,
    required,
    title,
    /** Mindestbreite des Portals (px) — für lange Optionstexte breiter als der Trigger */
    menuMinWidth = 0,
    /** Menü am rechten Trigger-Rand ausrichten (sinnvoll in rechten Toolbars) */
    menuAlign = 'start',
    'aria-label': ariaLabel,
    ...props
  },
  ref
) {
  const options = useMemo(() => collectOptions(children), [children]);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() =>
    defaultValue != null ? String(defaultValue) : options[0]?.value ?? ''
  );
  const currentValue = String(isControlled ? value ?? '' : internalValue);
  const selected = options.find((o) => o.value === currentValue) || options[0];
  const selectedLabel = labelText(selected?.label) || '';

  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 240 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const setTriggerRefs = useCallback(
    (node) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  const updateMenuPos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const preferBelow = spaceBelow >= 120 || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(120, Math.min(280, preferBelow ? spaceBelow : spaceAbove));
    const minW = Math.max(0, Number(menuMinWidth) || 0);
    const width = Math.min(
      Math.max(rect.width, minW),
      Math.max(120, window.innerWidth - 16)
    );
    const rawLeft =
      menuAlign === 'end' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(8, rawLeft), window.innerWidth - width - 8);
    const top = preferBelow
      ? rect.bottom + gap
      : Math.max(8, rect.top - gap - maxHeight);
    setMenuPos({
      top,
      left,
      width,
      maxHeight,
    });
  }, [menuAlign, menuMinWidth]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [open, updateMenuPos]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const emitChange = (nextValue) => {
    if (!isControlled) setInternalValue(nextValue);
    if (onChange) {
      onChange({
        target: { value: nextValue, name: name || undefined },
        currentTarget: { value: nextValue, name: name || undefined },
      });
    }
  };

  const pick = (opt) => {
    if (opt.disabled) return;
    emitChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const openMenu = () => {
    if (disabled) return;
    updateMenuPos();
    setOpen(true);
  };

  return (
    <div ref={rootRef} className={`relative w-full min-w-0 ${wrapperClassName}`.trim()}>
      {name ? (
        <input type="hidden" name={name} value={currentValue} required={required} />
      ) : null}
      <button
        {...props}
        ref={setTriggerRefs}
        type="button"
        id={id}
        title={title || selectedLabel}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel || selectedLabel || 'Auswahl'}
        className={`appearance-none pr-8 text-left truncate w-full min-w-0 max-w-full ${className}`.trim()}
        onMouseDown={(e) => {
          onMouseDown?.(e);
        }}
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else openMenu();
        }}
        onFocus={(e) => {
          onFocus?.(e);
        }}
        onBlur={(e) => {
          // Blur erst nach Auswahl / Außenklick — nicht schließen wenn Fokus ins Menü wandert
          const next = e.relatedTarget;
          if (menuRef.current?.contains(next)) return;
          if (rootRef.current?.contains(next)) return;
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (open) setOpen(false);
            else openMenu();
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) openMenu();
          }
          onKeyDown?.(e);
        }}
      >
        <span className="block truncate [&_svg]:inline-block [&_svg]:align-middle">
          {selected?.label != null && selected.label !== false
            ? selected.label
            : selectedLabel || '\u00a0'}
        </span>
      </button>
      <ChevronDown
        className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 ${chevronSize} text-neutral-400 dark:text-neutral-500 transition-transform duration-200 ease-out ${
          open && !disabled ? 'rotate-180' : ''
        } ${disabled ? 'opacity-40' : ''} ${chevronClassName}`.trim()}
      />

      {open && !disabled
        ? createPortal(
            <div
              ref={menuRef}
              id={listId}
              role="listbox"
              data-select-field-menu=""
              aria-activedescendant={
                selected ? `${listId}-opt-${selected.value}` : undefined
              }
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                maxHeight: menuPos.maxHeight,
                zIndex: 240,
              }}
              className="overflow-y-auto overscroll-contain rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1 scale-in"
            >
              {options.map((opt) => {
                const active = opt.value === currentValue;
                const text = labelText(opt.label);
                const rich =
                  opt.label != null &&
                  typeof opt.label !== 'string' &&
                  typeof opt.label !== 'number';
                return (
                  <button
                    key={`${opt.value}::${text}`}
                    id={`${listId}-opt-${opt.value}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={opt.disabled}
                    title={text}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm min-w-0 disabled:opacity-40 ${
                      active
                        ? 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200'
                        : 'text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <span
                      className={`flex-1 min-w-0 truncate whitespace-nowrap leading-snug ${
                        rich ? 'inline-flex items-center gap-2' : ''
                      }`}
                    >
                      {rich ? opt.label : text}
                    </span>
                    {active ? (
                      <Check className="w-3.5 h-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <span className="w-3.5 h-3.5 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
});

export default SelectField;
